import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CURRENCIES } from '../mock';
import { formatMoney } from '../lib/money';
import { ordersApi } from '../services/api';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const { user } = useAuth();

  // Currency
  const [currency, setCurrency] = useState(() => {
    try {
      const c = localStorage.getItem('swiftdash_currency');
      return c ? JSON.parse(c) : CURRENCIES[0];
    } catch { return CURRENCIES[0]; }
  });
  useEffect(() => {
    localStorage.setItem('swiftdash_currency', JSON.stringify(currency));
  }, [currency]);

  // Hydrate live FX rates on mount so displayed prices track the real market.
  // Backend endpoint returns { rates: {CODE: 1_USD_in_CODE, ...} } and we
  // patch each CURRENCIES entry's .rate in place. Fallback rates in mock.js
  // are used until this resolves (and if the network call fails).
  const [liveRates, setLiveRates] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/fx-rates?base=USD`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive || !data?.rates) return;
        setLiveRates(data.rates);
        // Refresh the selected currency's rate if it's stale
        setCurrency((cur) => {
          const live = data.rates[cur.code];
          return live && Number.isFinite(live) ? { ...cur, rate: live } : cur;
        });
      })
      .catch(() => { /* keep static fallback */ });
    return () => { alive = false; };
  }, []);

  // Auto-detect user's country/currency on first visit (IP-based). Only
  // applies if the user hasn't manually picked a currency yet.
  useEffect(() => {
    let alive = true;
    const hasManualPick = localStorage.getItem('swiftdash_currency_manual');
    if (hasManualPick) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo/detect`)
      .then((r) => r.json())
      .then((geo) => {
        if (!alive || !geo?.currency) return;
        const match = CURRENCIES.find((c) => c.code === geo.currency);
        if (match) {
          setCurrency(match);
        }
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  // Remember when the user manually picks so we stop overriding.
  const setCurrencyManual = (c) => {
    localStorage.setItem('swiftdash_currency_manual', '1');
    setCurrency(c);
  };

  const currencyOptions = useMemo(() => (
    CURRENCIES.map((c) => (liveRates?.[c.code] ? { ...c, rate: liveRates[c.code] } : c))
  ), [liveRates]);

  // Cart — scoped per-user so two accounts on the same browser don't share items.
  // Guests use a separate `__guest__` bucket that's migrated on login.
  // NOTE: The auth `user` object exposes `id` (not `user_id`) — reading the
  // wrong key here silently collapsed every signed-in user under
  // `swiftdash_cart_undefined`, which meant carts were shared across accounts.
  const userKey = user?.id || user?.user_id;
  const cartStorageKey = useMemo(() => `swiftdash_cart_${userKey || '__guest__'}`, [userKey]);
  const CART_TTL_MS = 48 * 60 * 60 * 1000; // baskets auto-expire after 48h
  const readStoredCart = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?._ts && Date.now() - parsed._ts > CART_TTL_MS) { localStorage.removeItem(key); return null; }
      return parsed;
    } catch { return null; }
  };
  const [cart, setCart] = useState(() => {
    // On first mount, AuthContext is still loading — so we read the guest cart.
    // The next effect (cartStorageKey-aware) will swap to the user's cart once auth resolves.
    return readStoredCart('swiftdash_cart___guest__') || { vendorId: null, vendorName: null, items: [] };
  });
  // Re-load cart whenever the active user changes (login / logout). If a guest
  // had items in their cart and then logged in, merge them into the user cart
  // so they don't disappear at sign-up time.
  useEffect(() => {
    // A just-restored basket (cancelled card payment) must not be wiped by
    // this hydration effect when auth resolves and the storage key flips —
    // Cart.jsx's restore runs BEFORE the user cart key is known.
    if (restoredRef.current) {
      restoredRef.current = cartStorageKey === 'swiftdash_cart___guest__';
      return;
    }
    try {
      // One-time migration of the legacy `swiftdash_cart_undefined` bucket that
      // was created by an older bug (see cartStorageKey comment). Move it to
      // the current user cart the first time this user signs in after the fix.
      const legacy = localStorage.getItem('swiftdash_cart_undefined');
      if (legacy && cartStorageKey !== 'swiftdash_cart_undefined' && cartStorageKey !== 'swiftdash_cart___guest__') {
        const already = localStorage.getItem(cartStorageKey);
        if (!already) localStorage.setItem(cartStorageKey, legacy);
        localStorage.removeItem('swiftdash_cart_undefined');
      }
      const userCart = readStoredCart(cartStorageKey);
      if (cartStorageKey !== 'swiftdash_cart___guest__') {
        const guestCart = readStoredCart('swiftdash_cart___guest__');
        if (guestCart && !userCart) {
          // Migrate guest -> user once on first login.
          localStorage.setItem(cartStorageKey, JSON.stringify(guestCart));
          localStorage.removeItem('swiftdash_cart___guest__');
          setCart(guestCart);
          return;
        }
      }
      setCart(userCart || { vendorId: null, vendorName: null, items: [] });
    } catch { setCart({ vendorId: null, vendorName: null, items: [] }); }
  }, [cartStorageKey]);
  useEffect(() => {
    localStorage.setItem(cartStorageKey, JSON.stringify({ ...cart, _ts: Date.now() }));
  }, [cart, cartStorageKey]);

  // Basket backup around card payments — a failed/cancelled Stripe session can
  // restore the exact basket (within the same 48h TTL) so the customer retries.
  const restoredRef = useRef(false);
  const backupCart = (orderId) => {
    try { localStorage.setItem('swiftdash_cart_backup', JSON.stringify({ cart, orderId, ts: Date.now() })); } catch { /* */ }
  };
  const restoreCartBackup = () => {
    try {
      const raw = localStorage.getItem('swiftdash_cart_backup');
      if (!raw) return false;
      const { cart: saved, ts } = JSON.parse(raw);
      if (!saved?.items?.length || (ts && Date.now() - ts > CART_TTL_MS)) {
        localStorage.removeItem('swiftdash_cart_backup');
        return false;
      }
      setCart(saved);
      restoredRef.current = true;
      localStorage.removeItem('swiftdash_cart_backup');
      return true;
    } catch { return false; }
  };

  const addToCart = (vendor, item) => {
    setCart((prev) => {
      if (prev.vendorId && prev.vendorId !== vendor.id && prev.items.length) {
        const ok = window.confirm('Your cart has items from another store. Replace cart?');
        if (!ok) return prev;
        return { vendorId: vendor.id, vendorName: vendor.name, vendorCurrency: vendor.currency, items: [{ ...item, qty: 1 }] };
      }
      const existing = prev.items.find((i) => i.id === item.id);
      if (existing) {
        return {
          ...prev,
          vendorId: vendor.id,
          vendorName: vendor.name,
          vendorCurrency: vendor.currency,
          items: prev.items.map((i) => (i.id === item.id ? { ...i, qty: i.qty + 1 } : i)),
        };
      }
      return {
        vendorId: vendor.id,
        vendorName: vendor.name,
        vendorCurrency: vendor.currency,
        items: [...prev.items, { ...item, qty: 1 }],
      };
    });
  };

  const updateQty = (itemId, delta) => {
    setCart((prev) => {
      const items = prev.items
        .map((i) => (i.id === itemId ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0);
      return { ...prev, items, vendorId: items.length ? prev.vendorId : null, vendorName: items.length ? prev.vendorName : null };
    });
  };

  const removeFromCart = (itemId) => {
    setCart((prev) => {
      const items = prev.items.filter((i) => i.id !== itemId);
      return { ...prev, items, vendorId: items.length ? prev.vendorId : null, vendorName: items.length ? prev.vendorName : null };
    });
  };

  // Marketplace purchase → seed the cart with the hidden seller-vendor + item.
  // mode: 'full' (pay everything via ShipitNow) | 'delivery_only' (pay seller directly).
  const startMarketplaceCheckout = (vendor, item, mode) => {
    setCart({
      vendorId: vendor.id, vendorName: vendor.name, vendorCurrency: vendor.currency,
      marketplaceMode: mode, items: [{ ...item, qty: 1 }],
    });
  };

  const clearCart = () => setCart({ vendorId: null, vendorName: null, items: [] });

  // Orders (server-backed)
  const [orders, setOrders] = useState([]);
  const refreshOrders = useCallback(async () => {
    if (!user) { setOrders([]); return; }
    try {
      const data = await ordersApi.list();
      setOrders(data);
    } catch (e) {
      setOrders([]);
    }
  }, [user]);

  useEffect(() => { refreshOrders(); }, [refreshOrders]);

  const placeOrder = async (payload) => {
    const created = await ordersApi.create(payload);
    setOrders((prev) => [created, ...prev]);
    return created;
  };

  // Delivery address (used for 25-km radius enforcement and Checkout prefill)
  const [delivery, setDelivery] = useState(() => {
    try {
      const d = localStorage.getItem('swiftdash_delivery');
      return d ? JSON.parse(d) : { address: '', lat: null, lng: null };
    } catch { return { address: '', lat: null, lng: null }; }
  });
  useEffect(() => { localStorage.setItem('swiftdash_delivery', JSON.stringify(delivery)); }, [delivery]);

  // Fulfillment preference (delivery | pickup). Set globally on Home so it
  // carries through to Checkout. Persisted across reloads.
  const [fulfillment, setFulfillment] = useState(() => {
    const f = localStorage.getItem('swiftdash_fulfillment');
    return f === 'pickup' ? 'pickup' : 'delivery';
  });
  useEffect(() => { localStorage.setItem('swiftdash_fulfillment', fulfillment); }, [fulfillment]);

  const fetchOrder = (id) => ordersApi.get(id);

  // No FX conversion — amounts are always shown in their own transaction
  // currency (CAD for Canadian stores, NGN for Nigerian stores).
  const format = (amount, ccy) => formatMoney(amount, ccy);

  const cartCount = useMemo(() => cart.items.reduce((a, b) => a + b.qty, 0), [cart]);
  const cartTotal = useMemo(() => cart.items.reduce((a, b) => a + b.price * b.qty, 0), [cart]);

  return (
    <AppContext.Provider
      value={{
        currency, setCurrency: setCurrencyManual, currencyOptions,
        cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartTotal,
        backupCart, restoreCartBackup, startMarketplaceCheckout,
        orders, refreshOrders, placeOrder, fetchOrder,
        delivery, setDelivery,
        fulfillment, setFulfillment,
        format,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
